import { useQuery } from '@apollo/client';
import { ColumnDef } from '@tanstack/react-table';
import Stars from '@website/components/directoryTheme/Stars';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  AdminReviewStatsDocument,
  AdminReviewStatsQuery,
} from '@website/types/graphqlOperations';
import Link from 'next/link';
import { useMemo } from 'react';
import DataTable from '../DataTable';

export default function AdminReviewStats() {
  const { data, loading, error } = useQuery(AdminReviewStatsDocument, {
    fetchPolicy: 'cache-first',
    pollInterval: 1000 * 60 * 60, // 1 hour
  });

  const tripsStatsTable: ColumnDef<
    AdminReviewStatsQuery['trips']['items'][number]
  >[] = useMemo(
    () =>
      [
        {
          header: 'Trip',
          id: 'title',
          size: 500,
          footer: 'All Trips',
          enableSorting: true,
          cell: ({ row }) => {
            return (
              <Link
                href={{
                  pathname: `/trips/${row.original.urlSlug}`,
                }}
              >
                {row.original.title}
              </Link>
            );
          },
        },
        {
          header: 'Total Reviews',
          id: 'reviewCount',
          size: 150,
          enableSorting: true,
          accessorFn: (row) => row.reviewStatistics.reviewCount,
          footer: data?.reviewStatistics.reviewCount.toString(),
        },
        {
          header: 'Average Rating',
          size: 200,
          cell: ({ row }) => {
            const { fiveStarRatingAverage } = row.original.reviewStatistics;

            return (
              <Stars
                size="sm"
                color="text-primary"
                stars={fiveStarRatingAverage}
              />
            );
          },
          footer: () => {
            return (
              <Stars
                size="sm"
                color="text-primary"
                stars={data?.reviewStatistics.fiveStarRatingAverage}
              />
            );
          },
        },

        {
          header: '5 Stars',
          size: 150,
          accessorFn: (row) =>
            row.reviewStatistics.starsReviewBreakdown.fiveStar,
          footer:
            data?.reviewStatistics.starsReviewBreakdown.fiveStar.toString(),
        },
        {
          header: '4 Stars',
          size: 150,
          accessorFn: (row) =>
            row.reviewStatistics.starsReviewBreakdown.fourStar,
          footer:
            data?.reviewStatistics.starsReviewBreakdown.fourStar.toString(),
        },
        {
          header: '3 Stars',
          size: 150,
          accessorFn: (row) =>
            row.reviewStatistics.starsReviewBreakdown.threeStar,
          footer:
            data?.reviewStatistics.starsReviewBreakdown.threeStar.toString(),
        },
        {
          header: '2 Stars',
          size: 150,
          accessorFn: (row) =>
            row.reviewStatistics.starsReviewBreakdown.twoStar,
          footer:
            data?.reviewStatistics.starsReviewBreakdown.twoStar.toString(),
        },
        {
          header: '1 Star',
          size: 150,
          accessorFn: (row) =>
            row.reviewStatistics.starsReviewBreakdown.oneStar,
          footer:
            data?.reviewStatistics.starsReviewBreakdown.oneStar.toString(),
        },
      ] satisfies ColumnDef<AdminReviewStatsQuery['trips']['items'][number]>[],
    [
      data?.reviewStatistics.fiveStarRatingAverage,
      data?.reviewStatistics.reviewCount,
      data?.reviewStatistics.starsReviewBreakdown.fiveStar,
      data?.reviewStatistics.starsReviewBreakdown.fourStar,
      data?.reviewStatistics.starsReviewBreakdown.oneStar,
      data?.reviewStatistics.starsReviewBreakdown.threeStar,
      data?.reviewStatistics.starsReviewBreakdown.twoStar,
    ],
  );

  const tripStats = useMemo(() => {
    // we want to filter out trips that don't have reviews
    return data?.trips.items
      .filter((trip) => trip.reviewStatistics)
      .sort((a, b) => {
        // sort by review count first, then by title
        if (a.reviewStatistics.reviewCount > b.reviewStatistics.reviewCount) {
          return -1;
        }
        if (a.reviewStatistics.reviewCount < b.reviewStatistics.reviewCount) {
          return 1;
        }
        return a.title.localeCompare(b.title);
      });
  }, [data?.trips.items]);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <DataTable
      data={tripStats}
      columns={tripsStatsTable}
      showFooter
      sorting={[
        {
          id: 'reviewCount',
          desc: true,
        },
        {
          id: 'title',
          desc: false,
        },
      ]}
    />
  );
}
